import styled from '@emotion/styled';
import ReactQRCode from 'qrcode.react';
import type { FC } from 'react';
import { Button as BSButton, FormControl, InputGroup } from 'react-bootstrap';
import { IconCopy } from '../../Icon';
import { copyIntoClipBoard } from '../../utils/Utils';
import { Center } from '../MetaComponent';

interface Props {
  value: string;
  size?: number;
  withLinkField?: boolean;
  withDownloadButton?: boolean;
  isClickable?: boolean;
  filename?: string;
  linklength?: number | null;
}

export const QRCode: FC<Props> = ({
  value,
  size = 128,
  withLinkField = true,
  withDownloadButton = false,
  isClickable = false,
  filename = 'qrcode',
  linklength = null,
}) => {
  const qrcodeComponent = <ReactQRCode value={value} size={size} id="qrpasscode" />;

  return (
    <Container>
      <QRCodeWrapper isClickable={isClickable} size={size}>
        {isClickable ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {qrcodeComponent}
          </a>
        ) : (
          qrcodeComponent
        )}
      </QRCodeWrapper>
      {withDownloadButton && (
        <Button variant="outline-primary" onClick={() => downloadQR(filename)}>
          Download QR-Code
        </Button>
      )}
      {withLinkField && <LinkField url={value} linklength={linklength} isClickable={isClickable} />}
    </Container>
  );
};

const downloadQR = (filename: string) => {
  const canvas = document.getElementById('qrpasscode') as HTMLCanvasElement;
  const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
  const downloadLink = document.createElement('a');

  downloadLink.href = pngUrl;
  downloadLink.download = `${filename}.png`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

interface LinkFieldProps {
  url: string;
  linklength: number | null;
  isClickable?: boolean;
}

function LinkField({ url, linklength, isClickable = false }: LinkFieldProps) {
  const handleClick = () => {
    copyIntoClipBoard(url);
  };
  const input = (
    <Input
      type="text"
      disabled
      value={url}
      clickable={isClickable.toString()}
      linklength={linklength || url.length}
      style={{ flexWrap: 'nowrap' }}
    />
  );

  return (
    <div className="monospace-font">
      <InputGroup>
        {isClickable ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {input}
          </a>
        ) : (
          input
        )}
        <InputGroup.Append>
          <Button variant="outline-secondary" onClick={handleClick}>
            <IconCopy />
          </Button>
        </InputGroup.Append>
      </InputGroup>
    </div>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Button = styled(BSButton)`
  margin-bottom: 20px;
`;

const Input = styled(FormControl)<{ clickable: string; linklength: number }>`
  cursor: ${({ clickable }) => (clickable === 'true' ? 'pointer' : 'unset')};
  width: calc(${({ linklength }) => `${linklength}ch`} + 24px) !important;
`;

const QRCodeWrapper = styled(Center)<{ isClickable: boolean; size: number }>`
  background: linear-gradient(to right, #000000 4px, transparent 4px) 0 0,
    linear-gradient(to right, #000000 4px, transparent 4px) 0 100%,
    linear-gradient(to left, #000000 4px, transparent 4px) 100% 0,
    linear-gradient(to left, #000000 4px, transparent 4px) 100% 100%,
    linear-gradient(to bottom, #000000 4px, transparent 4px) 0 0,
    linear-gradient(to bottom, #000000 4px, transparent 4px) 100% 0,
    linear-gradient(to top, #000000 4px, transparent 4px) 0 100%,
    linear-gradient(to top, #000000 4px, transparent 4px) 100% 100%;
  background-repeat: no-repeat;
  background-size: 20px 20px;
  padding: ${({ size }) => `${size * 0.1}px;`};
  margin-bottom: 20px;
  ${({ isClickable }) => (isClickable ? 'cursor: pointer' : '')}
`;
